import React, { useState } from "react";
import { Modal, Box, Text, ModalOverlay, ModalContent, Input, useToast, VStack, Button, Textarea, SimpleGrid, Image, AspectRatio, Flex, Select } from "@chakra-ui/react";
import { UseFormReturnType } from "@mantine/form";
import Form from "../custom/Form";
import { ProductInitialValueType } from "./AddProduct";
import { UseMutationResult } from "react-query";
import { DefaultPricing } from "./DefaultPricing";
import { CustomPricing } from "./CustomPricing";

type AddProductModalProps = {
  mutation: UseMutationResult<any, any, any, unknown>;
  isOpen: boolean;
  onClose: () => void;
  form: UseFormReturnType<any, (values: any) => any>,
  handleMutation: (values: ProductInitialValueType) => void
}

export type PricingType = 'default' | 'custom'

export default function AddProductModal({
  mutation,
  isOpen,
  onClose,
  form,
  handleMutation
}: AddProductModalProps) {
  const toast = useToast()
  const [pricingType, setPricingType] = useState<PricingType>('default')

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    if (files.length > 4) {
      toast({
        title: "You can only upload up to 4 images",
        status: 'error',
        isClosable: true,
        duration: 5000,
      });
      return;
    }

    const newImages: string[] = [];

    const readFile = (index: number) => {
      const reader = new FileReader();

      reader.onload = () => {
        const result = reader.result as string;
        newImages.push(result);

        // Check if all files have been read
        if (newImages.length === files.length) {
          form.setValues({
            ...form.values,
            images: newImages,
          });
        } else {
          // Read the next file
          readFile(index + 1);
        }
      };

      // Read the current file
      reader.readAsDataURL(files[index]);
    };

    // Start reading the first file
    readFile(0);
  };

  return (
    <>
      <Modal size='xl' isOpen={isOpen} onClose={onClose}>
        <ModalOverlay
          bg='none'
          backdropFilter='blur(10px)'
          style={{ filter: 'brightness(50%)' }}
        />

        <ModalContent>
          <Form onSubmit={form.onSubmit((values) => handleMutation(values))}>
            <Box p={5}>
              <Text fontSize='2xl' fontWeight='semibold' color='gray.600'>
                New Product
              </Text>

              <VStack w='100%' mt='5' spacing='5'>
                <Box w='100%'>
                  <Text fontWeight='semibold' color='gray.500'>
                    Product Name
                  </Text>

                  <Box mt='2'>
                    <Input
                      {...form.getInputProps('name')}
                      disabled={mutation.isLoading}
                      size='lg' mt={1} placeholder="Air Jordan Mid 1"
                      border={form.errors.name ? '2px' : '1px'}
                      borderColor={form.errors.name ? 'red.500' : 'gray.500'} color='gray.500'
                      focusBorderColor={form.errors.name ? 'red.500' : 'gray.500'}
                      _hover={{ outlineColor: 'transparent' }}
                    />

                    {form.errors.name &&
                      <Text fontWeight='semibold' mt={1} color='red.500'>
                        {form.errors.name}
                      </Text>
                    }
                  </Box>
                </Box>

                <Box w='100%'>
                  <Text fontWeight='semibold' color='gray.500'>
                    Product Description
                  </Text>

                  <Box mt='2'>
                    <Textarea
                      {...form.getInputProps('description')}
                      disabled={mutation.isLoading} maxH='150px'
                      size='lg' mt={1} placeholder="This iteration of the AJ1 reimagines Mike's first signature model with a fresh mix of colors."
                      border={form.errors.description ? '2px' : '1px'}
                      borderColor={form.errors.description ? 'red.500' : 'gray.500'} color='gray.500'
                      focusBorderColor={form.errors.description ? 'red.500' : 'gray.500'}
                      _hover={{ outlineColor: 'transparent' }}
                    />

                    {form.errors.description &&
                      <Text fontWeight='semibold' mt={1} color='red.500'>
                        {form.errors.description}
                      </Text>
                    }
                  </Box>
                </Box>

                <Box w='100%'>
                  <Flex justifyContent='space-between' alignItems='start'>
                    <Box w='100%'>
                      <Text fontWeight='semibold' color='gray.500'>
                        Product Price
                      </Text>
                    </Box>

                    <Select
                      color='gray.500'
                      border='1px'
                      w='200px'
                      borderColor='gray.500'
                      focusBorderColor='#37A75B'
                      value={pricingType}
                      onChange={({ target }) => {
                        setPricingType(target.value as PricingType)
                      }}
                    >
                      <option value='default'>Default</option>
                      <option value='custom'>Custom</option>
                    </Select>
                  </Flex>

                  <Box mt='2'>
                    {pricingType === 'default' ? (
                      <DefaultPricing
                        form={form}
                        mutation={mutation}
                      />
                    ) : (
                      <CustomPricing
                        form={form}
                        mutation={mutation}
                      />
                    )}
                  </Box>
                </Box>

                <Box w='100%'>
                  <Flex justifyContent='space-between' alignItems='start'>
                    <Text fontWeight='semibold' color='gray.500'>
                      Product Images
                    </Text>

                    <Button
                      isLoading={mutation.isLoading}
                      onClick={() => {
                        document.getElementById('file_input')?.click()
                      }}
                      bg='gray.500'
                      _hover={{ backgroundColor: 'gray.600' }}
                      color='white'
                    >
                      Attach Images
                    </Button>
                  </Flex>

                  <Box mt={5}>
                    <Box display='none'>
                      <input
                        type='file'
                        id='file_input'
                        accept='image/*'
                        onChange={handleImageChange}
                        multiple
                      />
                    </Box>

                    {form.errors.images &&
                      <Text fontWeight='semibold' mt={1} color='red.500'>
                        {form.errors.images}
                      </Text>
                    }

                    <SimpleGrid columns={[2, 2, 2, 4]} spacing={3}>
                      {form.values.images.map((image: string, index: number) => (
                        <AspectRatio
                          maxW='400px'
                          ratio={4 / 3}
                          key={index}
                          overflow='hidden'
                          borderRadius="10px"
                        >
                          <Image
                            src={image}
                            objectPosition='center'
                            objectFit='cover'
                            alt={`Product Image ${index}`}
                            sx={{
                              transition: 'transform 1s linear'
                            }}
                            _hover={{
                              transform: 'scale(1.1)',
                              filter: 'brightness(70%)',
                            }}
                          />
                        </AspectRatio>
                      ))}
                    </SimpleGrid>
                  </Box>
                </Box>

                <Box w='100%' mt={5}>
                  <Button
                    isLoading={mutation.isLoading}
                    type='submit'
                    bg='#37A75B' size="lg"
                    w='100%' color='white'
                    _hover={{ backgroundColor: mutation.isLoading ? '#37A75B' : '#046425' }}
                  >
                    Add Product
                  </Button>
                </Box>
              </VStack>
            </Box>
          </Form>
        </ModalContent>
      </Modal>
    </>
  )
}